import React from "react";
import {
  SettingOutlined,
  AuditOutlined,
  TeamOutlined,
  SafetyOutlined,
  LineChartOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const coreCards = [
  {
    title: "Configuration",
    description: "Manage tenant app configurations, aliases, and feature flags across environments",
    icon: <SettingOutlined />,
    path: "/configuration/editor",
    iconBg: "#eef2ff",
    iconColor: "#4f46e5",
  },
  {
    title: "Audit Logs",
    description: "Track changes, view modification history, and monitor who changed what and when",
    icon: <AuditOutlined />,
    path: "/audit-logs",
    iconBg: "#ecfdf5",
    iconColor: "#059669",
  },
];

const accessCards = [
  {
    title: "Users",
    description: "Manage user accounts, assign roles, and control access across environments",
    icon: <TeamOutlined />,
    path: "/users",
    perm: "users:manage",
    iconBg: "#fff7ed",
    iconColor: "#ea580c",
  },
  {
    title: "Roles",
    description: "Define roles and permissions that govern what users can see and do",
    icon: <SafetyOutlined />,
    path: "/roles",
    perm: "roles:edit",
    iconBg: "#fdf4ff",
    iconColor: "#9333ea",
  },
];

const analyticsCards = [
  {
    title: "Google Analytics",
    description: "View traffic, user behaviour, and engagement metrics from Google Analytics",
    icon: <LineChartOutlined />,
    path: "/analytics/google",
    perm: "analytics:view_google_analytics",
    iconBg: "#fff1f2",
    iconColor: "#e11d48",
  },
  {
    title: "Status Page",
    description: "Live health and 90-day uptime of production services from New Relic monitors",
    icon: <HeartOutlined />,
    path: "/status",
    perm: "monitoring:view_status_page",
    iconBg: "#ecfdf5",
    iconColor: "#059669",
  },
];

function CardGrid({ cards, navigate }) {
  return (
    <div className="home-grid">
      {cards.map((card) => (
        <div
          key={card.path}
          className="home-card"
          onClick={() => navigate(card.path)}
        >
          <div
            className="home-card-icon"
            style={{ "--icon-bg": card.iconBg, "--icon-color": card.iconColor }}
          >
            {card.icon}
          </div>
          <h3 className="home-card-title">{card.title}</h3>
          <p className="home-card-desc">{card.description}</p>
        </div>
      ))}
    </div>
  );
}

export default function HomePage() {
  const { user, hasAnyPermission } = useAuth();
  const navigate = useNavigate();

  const visibleAccessCards = accessCards.filter((c) => hasAnyPermission(c.perm));
  const visibleAnalyticsCards = analyticsCards.filter((c) => hasAnyPermission(c.perm));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
        </h1>
        <p className="page-subtitle">SafetyQube Administration Panel</p>
      </div>

      <div className="home-section">
        <h2 className="home-section-title">Administration</h2>
        <CardGrid cards={coreCards} navigate={navigate} />
      </div>

      {visibleAccessCards.length > 0 && (
        <div className="home-section">
          <h2 className="home-section-title">Access Control</h2>
          <CardGrid cards={visibleAccessCards} navigate={navigate} />
        </div>
      )}

      {visibleAnalyticsCards.length > 0 && (
        <div className="home-section">
          <h2 className="home-section-title">Analytics</h2>
          <CardGrid cards={visibleAnalyticsCards} navigate={navigate} />
        </div>
      )}
    </div>
  );
}
